/**
 * Копия утилит отрисовки ArchiMate для core_x6 (без зависимости от diagram-js и core).
 */
import {
	LAYER_APPLICATION,
	LAYER_BUSINESS,
	LAYER_MOTIVATION,
	LAYER_STRATEGY,
	LAYER_IMP_MIG,
	LAYER_PHYSICAL,
	LAYER_TECHNOLOGY,
} from '../metamodel/Concept';

interface IRGBAColor {
	r: string;
	g: string;
	b: string;
	a: string;
}

interface IShape extends IBox {
	x?: number | null;
	y?: number | null;
}

interface IBox {
	width: number;
	height: number;
}

interface INodeBoxParams extends IBox {
	label: string;
	textVerticalAnchor: 'top' | 'middle' | 'bottom';
	reserveForIcon: boolean;
	font?: string;
}

function componentsToPath(segments: (string | number)[][]) {
	return segments.map(seg => seg.join(' ')).join(' ');
}

export const COLOR_LAYER_STRATEGY = {r: '245', g: '222', b: '170', a: '100'};
export const COLOR_LAYER_BUSINESS = {r: '255', g: '255', b: '175', a: '100'};
export const COLOR_LAYER_APPLICATION = {r: '175', g: '255', b: '255', a: '100'};
export const COLOR_LAYER_TECHNOLOGY = {r: '175', g: '255', b: '175', a: '100'};
export const COLOR_LAYER_PHYSICAL = {r: '175', g: '255', b: '175', a: '100'};
export const COLOR_LAYER_IMP_MIG = {r: '255', g: '224', b: '224', a: '100'};
export const COLOR_LAYER_MOTIVATION = {r: '204', g: '204', b: '255', a: '100'};

export const COLOR_LAYER_MAP = new Map([
	[LAYER_STRATEGY, COLOR_LAYER_STRATEGY],
	[LAYER_BUSINESS, COLOR_LAYER_BUSINESS],
	[LAYER_APPLICATION, COLOR_LAYER_APPLICATION],
	[LAYER_TECHNOLOGY, COLOR_LAYER_TECHNOLOGY],
	[LAYER_PHYSICAL, COLOR_LAYER_PHYSICAL],
	[LAYER_IMP_MIG, COLOR_LAYER_IMP_MIG],
	[LAYER_MOTIVATION, COLOR_LAYER_MOTIVATION],
]);

function between(x: number, min: number, max: number): boolean {
	return x >= min && x <= max;
}

export function toRgbaString(color: IRGBAColor): string | false {
	if (!color) return false;
	const r = Number(color.r);
	const g = Number(color.g);
	const b = Number(color.b);
	let a = 1;

	if (between(r, 0, 255) && between(g, 0, 255) && between(b, 0, 255)) {
		if (between(Number(color.a), 0, 100)) {
			a = Number(color.a) / 100;
		}

		return `rgba(${r}, ${g}, ${b}, ${a})`;
	}
	return false;
}

/**
 * Шестиугольник с диагональными срезами углов — нотация ArchiMate для элементов Motivation.
 */
export function getHexagonPath(shape: IShape): string {
	const x = shape.x ?? 0;
	const y = shape.y ?? 0;
	const width = shape.width;
	const height = shape.height;
	const cut = Math.min(width, height) * 0.15;

	const hexagonPath = [
		['M', x + cut, y],
		['l', width - cut * 2, 0],
		['l', cut, cut],
		['l', 0, height - cut * 2],
		['l', -cut, cut],
		['l', cut * 2 - width, 0],
		['l', -cut, -cut],
		['l', 0, cut * 2 - height],
		['l', cut, -cut],
		['z'],
	];

	return componentsToPath(hexagonPath);
}

export const GROUPING_TAB_W_FACTOR = 0.35;
export const GROUPING_TAB_W_MAX = 60;
const GROUPING_TAB_H_FACTOR = 0.3;
const GROUPING_TAB_H_MAX = 24;

/** Шрифт по умолчанию для оценки ширины подписи (согласно registerArchimateShapes, fontSize 12). */
const GROUPING_LABEL_FONT =
	'12px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

let _groupingMeasureCtx: CanvasRenderingContext2D | null = null;
function getGroupingMeasureContext(): CanvasRenderingContext2D | null {
	if (typeof document === 'undefined' || !document.createElement) return null;
	if (!_groupingMeasureCtx) {
		const c = document.createElement('canvas');
		_groupingMeasureCtx = c.getContext('2d');
	}

	return _groupingMeasureCtx;
}

/**
 * Максимальная ширина строки (по строкам переноса) для оценки размера язычка.
 */
export function measureGroupLabelMaxLineWidth(text: string, font = GROUPING_LABEL_FONT): number {
	if (text == null || String(text).trim() === '') return 0;
	const ctx = getGroupingMeasureContext();
	if (!ctx) return 0;
	ctx.font = font;
	let maxW = 0;
	for (const line of String(text).split(/\r?\n/)) {
		const m = ctx.measureText(line);
		if (m.width > maxW) maxW = m.width;
	}
	return maxW;
}

/**
 * Когда подпись у верхнего края, язычок растягиваем не уже ширины текста (+ поля);
 * иначе — встроенные коэффициенты (как раньше без tabBox).
 */
export function resolveGroupingTabBox(p: INodeBoxParams): IBox | undefined {
	const {width, height, label, textVerticalAnchor, reserveForIcon, font} = p;
	if (!width || !height) return undefined;
	if (textVerticalAnchor !== 'top') return undefined;
	if (!String(label ?? '').trim()) return undefined;
	const textW = measureGroupLabelMaxLineWidth(label, font);
	if (textW <= 0) return undefined;

	const tabH = Math.min(height * GROUPING_TAB_H_FACTOR, GROUPING_TAB_H_MAX);
	const defaultTabW = Math.min(width * GROUPING_TAB_W_FACTOR, GROUPING_TAB_W_MAX);
	/** 8px слева + 8px справа, как refX/refY 8 в подписи. */
	const horizontalPad = 16;
	let tabW = Math.max(defaultTabW, Math.ceil(textW + horizontalPad));

	if (reserveForIcon) {
		const iconW = Math.round(0.22 * width);
		const maxW = width - iconW - 6;
		tabW = Math.min(tabW, Math.max(1, maxW));
	}
	tabW = Math.max(1, Math.min(tabW, width - 1));
	return {width: tabW, height: tabH};
}

/**
 * Форма Group: язычок отделён от основной фигуры линией.
 */
export function getGroupingPath(shape: IShape, tabBox: IBox): string {
	const width = shape.width;
	const height = shape.height;
	let tabW;
	let tabH;
	if (tabBox) {
		tabW = tabBox.width;
		tabH = tabBox.height;
	} else {
		tabW = Math.min(width * GROUPING_TAB_W_FACTOR, GROUPING_TAB_W_MAX);
		tabH = Math.min(height * GROUPING_TAB_H_FACTOR, GROUPING_TAB_H_MAX);
	}

	const groupingPath = [
		['M', 0, 0],
		['l', tabW, 0],
		['l', 0, tabH],
		['l', -tabW, 0],
		['z'],
		['M', 0, tabH],
		['l', width, 0],
		['l', 0, height - tabH],
		['l', -width, 0],
		['z'],
	];

	return componentsToPath(groupingPath);
}
